import {
    Paragraph,
    TextRun,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    AlignmentType,
    HeightRule,
    TableLayoutType,
} from "docx";
import { fixColor, fitImage, fetchImageWithDimensions, FULL_WIDTH, SCALE, createCommonHeader, createCommonFooter } from "@/utils/wordGenerator/common.js";
import { formatedDate } from "@/utils/proposalValidation.js";

export const createCPTemp1 = async (cpData, startPage) => {
    const style = cpData.style || {};
    const sections = [];
    const projectsPerPage = 2;

    const BODY_HEIGHT = 14570;
    const SPACER_HEIGHT = 50;

    const SIDE_PAD_PERCENT = 3;
    const COL_WIDTH_PERCENT = 46.75;
    const GAP_WIDTH_PERCENT = 0.5;
    const IMG_HEIGHT = 4500;
    const CELL_MARGIN = 150;

    const actualColWidthDxa = Math.floor(FULL_WIDTH * (COL_WIDTH_PERCENT / 100)) - (CELL_MARGIN * 2);

    let currentAbsPage = startPage || 1;

    for (let i = 0; i < cpData.projects.length; i += projectsPerPage) {
        const pageProjects = cpData.projects.slice(i, i + projectsPerPage);

        const projectCells = [];
        for (const proj of pageProjects) {
            const children = [];

            // 1. Image
            const photo = proj.uploadedPhoto || proj.photoURL;
            if (photo) {
                const imgData = await fetchImageWithDimensions(photo);
                if (imgData) {
                    children.push(new Table({
                        width: { size: 100, type: WidthType.PERCENTAGE },
                        borders: {
                            top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                            left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                            insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                        },
                        rows: [
                            new TableRow({
                                height: { value: IMG_HEIGHT, rule: HeightRule.EXACT },
                                children: [
                                    new TableCell({
                                        children: [new Paragraph({
                                            alignment: AlignmentType.CENTER,
                                            children: [fitImage(imgData, actualColWidthDxa, IMG_HEIGHT, "cover")]
                                        })]
                                    })
                                ]
                            })
                        ]
                    }));
                    children.push(new Paragraph({ spacing: { after: 200 } }));
                }
            } else {
                children.push(new Table({
                    width: { size: 100, type: WidthType.PERCENTAGE },
                    borders: {
                        top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                        left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                        insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                    },
                    rows: [
                        new TableRow({
                            height: { value: IMG_HEIGHT, rule: HeightRule.EXACT },
                            children: [
                                new TableCell({
                                    shading: { fill: "FFFFFF" },
                                    children: []
                                })
                            ]
                        })
                    ]
                }));
                children.push(new Paragraph({
                    spacing: { after: 200 }
                }));
            }

            // 2. Project Name
            children.push(new Paragraph({
                children: [new TextRun({
                    text: proj.projectName || "N/A",
                    bold: style.heading?.bold || true,
                    italics: style.heading?.italic,
                    underline: style.heading?.underline ? {} : undefined,
                    size: (style.heading?.size || 14) * SCALE,
                    color: fixColor(style.heading?.color || "#003399"),
                    font: style.heading?.font
                })],
                spacing: { after: 120 }
            }));

            // 3. Location
            children.push(new Paragraph({
                children: [new TextRun({
                    text: `${proj.city || 'N/A'}, ${proj.stateProvince || 'N/A'}`,
                    size: (style.label?.size || 10) * SCALE,
                    color: fixColor(style.label?.color || "#545454"),
                    font: style.label?.font,
                    bold: style.label?.bold,
                    italics: style.label?.italic,
                    underline: style.label?.underline ? {} : undefined,
                })],
                spacing: { after: 120 }
            }));

            // 4. Client
            children.push(new Paragraph({
                children: [
                    new TextRun({
                        text: "Client: ",
                        bold: style.label?.bold || true,
                        italics: style.label?.italic,
                        underline: style.label?.underline ? {} : undefined,
                        size: (style.label?.size || 10) * SCALE,
                        color: fixColor(style.label?.color || "#545454"),
                        font: style.label?.font
                    }),
                    new TextRun({
                        text: proj.clientAccount?.accountName || 'N/A',
                        size: (style.text?.size || 10) * SCALE,
                        color: fixColor(style.text?.color || "#000000"),
                        font: style.text?.font,
                        bold: style.text?.bold,
                        italics: style.text?.italic,
                        underline: style.text?.underline ? {} : undefined,
                    })
                ],
                spacing: { after: 60 }
            }));

            // 5. Designers
            const designers = proj.designerAccounts?.map(d => d.accountName).join(', ') || 'N/A';
            children.push(new Paragraph({
                children: [
                    new TextRun({
                        text: "Designers: ",
                        bold: style.label?.bold || true,
                        italics: style.label?.italic,
                        underline: style.label?.underline ? {} : undefined,
                        size: (style.label?.size || 10) * SCALE,
                        color: fixColor(style.label?.color || "#545454"),
                        font: style.label?.font
                    }),
                    new TextRun({
                        text: designers,
                        size: (style.text?.size || 10) * SCALE,
                        color: fixColor(style.text?.color || "#000000"),
                        font: style.text?.font,
                        bold: style.text?.bold,
                        italics: style.text?.italic,
                        underline: style.text?.underline ? {} : undefined,
                    })
                ],
                spacing: { after: 60 }
            }));

            // 6. Construction Cost
            children.push(new Paragraph({
                children: [
                    new TextRun({
                        text: "Construction Cost: ",
                        bold: style.label?.bold || true,
                        italics: style.label?.italic,
                        underline: style.label?.underline ? {} : undefined,
                        size: (style.label?.size || 10) * SCALE,
                        color: fixColor(style.label?.color || "#545454"),
                        font: style.label?.font
                    }),
                    new TextRun({
                        text: `$${proj.value || 'N/A'}`,
                        size: (style.text?.size || 10) * SCALE,
                        color: fixColor(style.text?.color || "#000000"),
                        font: style.text?.font,
                        bold: style.text?.bold,
                        italics: style.text?.italic,
                        underline: style.text?.underline ? {} : undefined,
                    })
                ],
                spacing: { after: 60 }
            }));

            // 7. Construction Duration
            children.push(new Paragraph({
                children: [
                    new TextRun({
                        text: "Construction Duration: ",
                        bold: style.label?.bold || true,
                        italics: style.label?.italic,
                        underline: style.label?.underline ? {} : undefined,
                        size: (style.label?.size || 10) * SCALE,
                        color: fixColor(style.label?.color || "#545454"),
                        font: style.label?.font
                    }),
                    new TextRun({
                        text: `${formatedDate(proj.constructionStartDate) || 'N/A'} to ${formatedDate(proj.constructionCompletionDate) || 'N/A'}`,
                        size: (style.text?.size || 10) * SCALE,
                        color: fixColor(style.text?.color || "#000000"),
                        font: style.text?.font,
                        bold: style.text?.bold,
                        italics: style.text?.italic,
                        underline: style.text?.underline ? {} : undefined,
                    })
                ],
                spacing: { after: 300 }
            }));

            // 8. Description
            children.push(new Paragraph({
                children: [new TextRun({
                    text: proj.projectDetails || "",
                    size: (style.text?.size || 10) * SCALE,
                    color: fixColor(style.text?.color || "#545454"),
                    font: style.text?.font,
                    bold: style.text?.bold,
                    italics: style.text?.italic,
                    underline: style.text?.underline ? {} : undefined,
                })],
                spacing: { after: 200 }
            }));

            projectCells.push(new TableCell({
                width: { size: COL_WIDTH_PERCENT, type: WidthType.PERCENTAGE },
                shading: { fill: fixColor(style.colorScheme?.body || "EEEEEE") },
                margins: { top: 100, bottom: 100, left: 100, right: 100 },
                children: children
            }));
        }

        // Filler
        if (projectCells.length === 1) {
            projectCells.push(new TableCell({
                shading: { fill: fixColor(style.colorScheme?.body || "EEEEEE") },
                width: { size: COL_WIDTH_PERCENT, type: WidthType.PERCENTAGE }, children: []
            }));
        }

        const headerTable = createCommonHeader(cpData.title, fixColor(style.colorScheme?.header || "#545454"));
        const footerTable = createCommonFooter(currentAbsPage, fixColor(style.colorScheme?.footer || "#545454"));

        const bodyTable = new Table({
            width: { size: FULL_WIDTH, type: WidthType.DXA },
            layout: TableLayoutType.FIXED,
            borders: {
                top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
            },
            rows: [
                new TableRow({
                    height: { value: BODY_HEIGHT, rule: HeightRule.EXACT },
                    children: [
                        new TableCell({ width: { size: SIDE_PAD_PERCENT, type: WidthType.PERCENTAGE }, children: [] }),
                        projectCells[0],
                        new TableCell({
                            spacing: { after: 100, before: 100 },
                            width: { size: GAP_WIDTH_PERCENT, type: WidthType.PERCENTAGE },
                            children: []
                        }),
                        projectCells[1],
                        new TableCell({ width: { size: SIDE_PAD_PERCENT, type: WidthType.PERCENTAGE }, children: [] }),
                    ],
                }),
            ],
        });

        sections.push({
            properties: {
                page: {
                    margin: { top: 0, right: 0, bottom: 0, left: 0 },
                },
            },
            children: [
                headerTable,
                new Paragraph({ spacing: { before: SPACER_HEIGHT, after: 0 }, children: [] }),
                bodyTable,
                new Paragraph({ spacing: { before: SPACER_HEIGHT, after: 0 }, children: [] }),
                footerTable
            ],
        });
        currentAbsPage++;
    }
    return sections;
};
